/**
 * Composable for robust polling with stale detection and error recovery.
 *
 * @param {Function} callback - Async function to execute during poll
 * @param {Object} options - Configuration options
 * @param {number} options.staleTimeout - Time (ms) before forcing execution (default: 60_000)
 * @param {number} options.pollInterval - Poll frequency (ms) (default: 30_000)
 * @param {string} options.label - Debug label for logging
 * @returns {Object} - { startPolling, stopPolling }
 */
export const usePolling = (callback, options = {}) => {
  const {
    staleTimeout = 60_000,
    pollInterval = 30_000,
    label = "polling",
  } = options;

  let lastExecutionTime = Date.now();
  let errorCount = 0;
  let pollIntervalId = null;

  /**
   * Track successful execution and reset error count
   */
  const markSuccess = () => {
    lastExecutionTime = Date.now();

    errorCount = 0;
  };

  /**
   * Increment error count for retry backoff
   */
  const markError = () => {
    errorCount++;
  };

  /**
   * Execute callback with error tracking
   */
  const execute = async () => {
    try {
      await callback();

      markSuccess();
    } catch (error) {
      markError();

      console.error(`[${label}] Execution error:`, error);
    }
  };

  /**
   * Start polling with stale detection and error recovery
   */
  const startPolling = () => {
    if (pollIntervalId) return; // Prevent duplicate intervals

    pollIntervalId = setInterval(() => {
      const timeSinceLastExecution = Date.now() - lastExecutionTime;

      // If no updates in staleTimeout, force execution
      if (timeSinceLastExecution > staleTimeout) {
        console.warn(
          `🔄 [${label}] Stale (>${staleTimeout}ms), forcing execution...`,
        );

        execute();

        return;
      }

      // If errors occurred, retry with exponential backoff
      if (errorCount > 0) {
        const backoffDelay = Math.min(
          1000 * Math.pow(2, errorCount - 1),
          30_000,
        );

        console.warn(
          `🔄 [${label}] Error retry (attempt ${errorCount}, backoff ${backoffDelay}ms)...`,
        );

        setTimeout(() => execute(), backoffDelay);
      }
    }, pollInterval);
  };

  /**
   * Stop polling and cleanup
   */
  const stopPolling = () => {
    if (pollIntervalId) {
      clearInterval(pollIntervalId);

      pollIntervalId = null;
    }
  };

  return {
    startPolling,
    stopPolling,
    execute,
    markSuccess,
    markError,
  };
};
